import React, { useRef } from 'react';
import { usePerformanceSentinel } from '../hooks/usePerformanceSentinel';
import { useVisibilitySentinel } from '../hooks/useVisibilitySentinel';

/**
 * GlowingStar - A premium glowing star effect with rays and intense core.
 * Features performance and visibility monitoring to ensure stability.
 */
export const GlowingStar = ({ className = "" }: { className?: string }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const { isPanic } = usePerformanceSentinel();
    const { isVisible } = useVisibilitySentinel(containerRef);

    // If performance is bad, or component/tab is hidden, pause heavy effects
    const showAdvancedEffects = !isPanic && isVisible;

    return (
        <div ref={containerRef} className={`pointer-events-none select-none ${className}`}>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px]">

                {showAdvancedEffects && (
                    <>
                        {/* Primary Rays - Slow Rotation - Uneven stops for realism */}
                        <div className="absolute inset-0 animate-[spin_100s_linear_infinite] opacity-[0.12]">
                            <div
                                className="absolute inset-0"
                                style={{
                                    background: 'conic-gradient(from 0deg, transparent 0, #f97316 1deg, transparent 4deg, transparent 20deg, #f97316 21deg, transparent 24deg, transparent 45deg, #f97316 46deg, transparent 48deg, transparent 80deg, #f97316 81deg, transparent 85deg, transparent 120deg, #f97316 121deg, transparent 124deg, transparent 150deg, #f97316 151deg, transparent 155deg, transparent 190deg, #f97316 191deg, transparent 193deg, transparent 230deg, #f97316 231deg, transparent 236deg, transparent 270deg, #f97316 271deg, transparent 274deg, transparent 310deg, #f97316 311deg, transparent 315deg, transparent 340deg, #f97316 341deg, transparent 345deg, transparent 360deg)',
                                    maskImage: 'radial-gradient(circle, black 15%, transparent 70%)',
                                    WebkitMaskImage: 'radial-gradient(circle, black 15%, transparent 70%)',
                                }}
                            />
                        </div>

                        {/* Secondary Sparkle Rays - Faster - More sparse */}
                        <div className="absolute inset-0 animate-[spin_45s_linear_infinite_reverse] opacity-[0.08]">
                            <div
                                className="absolute inset-0"
                                style={{
                                    background: 'conic-gradient(from 45deg, transparent 0, #fb923c 0.5deg, transparent 3deg, transparent 60deg, #fb923c 60.5deg, transparent 64deg, transparent 130deg, #fb923c 130.5deg, transparent 135deg, transparent 210deg, #fb923c 210.5deg, transparent 215deg, transparent 300deg, #fb923c 300.5deg, transparent 305deg, transparent 360deg)',
                                    maskImage: 'radial-gradient(circle, black 5%, transparent 60%)',
                                    WebkitMaskImage: 'radial-gradient(circle, black 5%, transparent 60%)',
                                }}
                            />
                        </div>
                    </>
                )}

                {/* Outer Soft Haze - Keep always for presence, with performance check if needed */}
                <div className="absolute inset-0 bg-orange-500/10 rounded-full blur-[100px] opacity-40 transition-opacity duration-1000" />

                {/* Main Middle Glow */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[280px] h-[280px] bg-orange-500/15 rounded-full blur-[60px]" />

                {/* Intense Inner Core */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[160px] h-[160px] bg-orange-600/20 rounded-full blur-[40px]" />

                {/* Brightest Center Point */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-orange-50 rounded-full blur-[1px] opacity-80 shadow-[0_0_12px_rgba(255,255,255,0.7)]" />
            </div>
        </div>
    );
};
